import "../../../components/common/Tables/DataTable.css"
import React, {useEffect, useState} from "react";
import { AppDispatch } from "../../../redux/store";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Tooltip } from "@mui/material";
import { fetchSpaCommands } from "../../../redux/actions/commands.actions";
import RootState from "../../../redux/states/root.state";
import CancelCommandModal from "../../SpaCommands/components/CancelCommandModal";
import { formatRelativeTime, isExpired } from "../../../utils/relativeTime";
import { useReportTimeFormat } from "../reportTime";
import { DataTableFoot, SortButton } from "../../../components/common/Tables/DataTableParts";
import { CloseIcon } from "../../../components/common/Icons/Icons";

// Commands queued for spas: the Spa Commands page (all spas, with filters in the URL) and the
// Spa Report's command history (one spa over a date range). Rendered inside a DataTable card
// whose toolbar the page owns, so this is the scrolling table and its pager.

type Order = 'asc' | 'desc';
const headers = [
  {id:'expires_at', label:'Status', sortable:false},
  {id:'spa_id', label:'GUID', sortable:true},
  {id:'title', label:'Title', sortable:true},
  {id:'body', label:'Body', sortable:true},
  {id:'updated_at', label:'Last Updated', sortable:true},
  {id:'created_at', label:'Created', sortable:true},
  {id:'actions', label:'Cancel', sortable:false, align:'center'},
];

interface SpaCommandsTableProps {
    spa_guid?: string;
    startDate?: string;
    endDate?: string;
}

const SpaCommandsTable: React.FC<SpaCommandsTableProps> = ({spa_guid=null, startDate=null, endDate=null}) => {
    const dispatch: AppDispatch = useDispatch();
    const navigate = useNavigate();
    const {data:spaCommands, loading, meta} = useSelector((state: RootState) => state.commands);
    const formatTime = useReportTimeFormat();
    const [searchParams, setSearchParams] = useSearchParams();

    // Sorted by the API (read from the URL so the arrow survives a reload and every refresh)
    const [order, setOrder] = useState<Order>(searchParams.get('sort_direction') === 'desc' ? 'desc' : 'asc');
    const [orderBy, setOrderBy] = useState(searchParams.get('sort_by') ?? '');

    const [cancelModalOpen, setCancelModalOpen] = useState(false);
    const [selectedCommand, setSelectedCommand] = useState<any>(null);

    useEffect(() => {
      if (spa_guid && startDate && endDate) {
        dispatch(fetchSpaCommands({spa_id:spa_guid, params:`from=${startDate}&until=${endDate}`}))
      }
    },[dispatch,spa_guid,startDate,endDate]);

    const handleRequestSort = (header_id: string) => {
      const isAsc = orderBy === header_id && order === 'asc';
      const newOrder = isAsc ? 'desc' : 'asc';
      setOrder(newOrder);
      setOrderBy(header_id);
      searchParams.set("sort_direction", newOrder);
      searchParams.set("sort_by", header_id);
      setSearchParams(searchParams);
    };

    const page = searchParams.get("page") ? Number(searchParams.get("page")) : 1;
    const totalPages = meta?.total_pages ?? 1;
    const total = meta?.total ?? 0;

    // Merge rather than replace: a bare {page} dropped the active filters, the sort
    // and the auto-refresh interval every time you turned the page.
    const goToPage = (value: number) => {
      const next = new URLSearchParams(searchParams);
      next.set("page", String(value));
      setSearchParams(next);
    };

    const handleCancelCommand = (spaCommand: any) => {
        setSelectedCommand(spaCommand);
        setCancelModalOpen(true);
    };

    // Sent, or waiting on the spa until it expires
    const status = (spaCommand: any): {label: string; tone: string} => {
      if (spaCommand?.is_sent) return {label: 'Sent', tone: 'good'};
      if (!spaCommand?.expires_at) return {label: 'N/A', tone: 'neutral'};
      return isExpired(spaCommand.expires_at) ? {label: 'Expired', tone: 'neutral'} : {label: 'Pending', tone: 'caution'};
    };

    const renderCell = (id: string, spaCommand: any) => {
      switch (id) {
        case 'expires_at': {
          const {label, tone} = status(spaCommand);
          const pill = <span className={`dt-pill ${tone}`}>{label}</span>;
          return spaCommand?.expires_at ? (
            <Tooltip arrow title={<div>Expires {formatTime(spaCommand.expires_at)}<br/>{formatRelativeTime(spaCommand.expires_at)}</div>}>
              {pill}
            </Tooltip>
          ) : pill;
        }

        case 'spa_id':
          return spaCommand?.spa_id ? (
            <a
              href={`/spa/${spaCommand.spa_id}`}
              className="dt-guid"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="View spa"
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey) return;
                e.preventDefault();
                navigate(`/spa/${spaCommand.spa_id}`);
              }}
            >
              {spaCommand.spa_id}
            </a>
          ) : <span className="dt-na">—</span>;

        case 'title':
          return <span className="dt-strong">{spaCommand?.command?.title ?? '—'}</span>;

        case 'body': {
          const body = spaCommand?.custom_body ? JSON.stringify(spaCommand.custom_body) : '';
          return body
            ? <Tooltip title={body} arrow><span className="dt-body dt-mono">{body}</span></Tooltip>
            : <span className="dt-na">—</span>;
        }

        case 'updated_at':
          return spaCommand?.updated_at ? <span className="dt-date">{formatTime(spaCommand.updated_at)}</span> : <span className="dt-na">—</span>;

        case 'created_at':
          return spaCommand?.created_at ? <span className="dt-date">{formatTime(spaCommand.created_at)}</span> : <span className="dt-na">—</span>;

        case 'actions': {
          if (spaCommand?.is_sent) return <span className="dt-na">—</span>;
          const expired = isExpired(spaCommand?.expires_at);
          return (
            <Tooltip title={expired ? "Expired commands can't be cancelled" : "Cancel command"} arrow>
              <span>
                <button
                  type="button"
                  className="dt-act danger"
                  onClick={() => handleCancelCommand(spaCommand)}
                  disabled={expired || loading}
                  aria-label="Cancel command"
                >
                  <CloseIcon/>
                </button>
              </span>
            </Tooltip>
          );
        }

        default:
          return null;
      }
    };

    return (
      <>
        {cancelModalOpen && (
          <CancelCommandModal
            open={cancelModalOpen}
            setOpen={setCancelModalOpen}
            object={selectedCommand}
          />
        )}

        <div className="dt-scroll">
          {loading ? (
            <div className="dt-state"><span className="spinner" role="progressbar" aria-label="Loading commands"/></div>
          ) : spaCommands?.length > 0 ? (
            <table className="dt-table">
              <thead>
                <tr>
                  {headers.map((header)=>(
                    <th
                      key={header.id}
                      scope="col"
                      className={header.align ? 'dt-center' : undefined}
                      aria-sort={orderBy === header.id ? (order === 'asc' ? 'ascending' : 'descending') : undefined}
                    >
                      {header.sortable ? (
                        <SortButton label={header.label} active={orderBy === header.id} order={order} onClick={()=>handleRequestSort(header.id)}/>
                      ) : header.label}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {spaCommands.map((spaCommand: any, i: number)=>(
                  <tr key={spaCommand?.id ?? i}>
                    {headers.map((header)=>(
                      <td key={header.id} className={header.align ? 'dt-center' : undefined}>
                        {renderCell(header.id, spaCommand)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="dt-state">
              <div className="dt-empty-title">No Commands Found</div>
            </div>
          )}
        </div>

        <DataTableFoot
          shown={spaCommands?.length ?? 0}
          total={total}
          noun={total === 1 ? 'Spa Command' : 'Spa Commands'}
          page={page}
          totalPages={totalPages}
          loading={loading}
          onPage={goToPage}
        />
      </>
    );
};

export default SpaCommandsTable;
